/**
 * CSV Export Utility
 * Converts student data to CSV format with all details
 */

/**
 * Escape CSV field value
 */
const escapeCSV = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  
  const stringValue = String(value);
  
  // If value contains comma, newline, or quote, wrap in quotes and escape quotes
  if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  
  return stringValue;
};

/**
 * Format date for CSV
 */
const formatDate = (date) => {
  if (!date) return '';
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().split('T')[0]; // YYYY-MM-DD format
  } catch (e) {
    return '';
  }
};

/**
 * Get academic qualification details
 */
const getAcademicQualification = (qualifications, type) => {
  if (!qualifications || !Array.isArray(qualifications)) return null;
  const qual = qualifications.find(q => q.qualificationType === type);
  return qual || null;
};

/**
 * Convert students array to CSV format
 */
export const exportStudentsToCSV = (students) => {
  if (!students || students.length === 0) {
    return '';
  }

  // Define CSV headers with all student details
  const headers = [
    // Basic Information
    'Student ID',
    'Full Name',
    'First Name',
    'Middle Name',
    'Last Name',
    'Email',
    'Phone Number',
    'WhatsApp',
    'LinkedIn',
    'Date of Birth',
    'Gender',
    'Employee ID',
    'Experience Years',
    'Availability',
    
    // Address Information
    'Current Address',
    'Current Location',
    'Permanent Address',
    'Joining Kit Address',
    'Joining Kit Received',
    'Pincode',
    'Country',
    'State',
    'City',
    
    // Guardian/Parent Information
    'Guardian Name',
    'Guardian Contact',
    'Guardian Email',
    'Parents Name',
    'Parents Email',
    'Parents Phone',
    
    // Education - 10th
    '10th Qualification Type',
    '10th University/College',
    '10th Specialization',
    '10th Percentage/CGPA',
    '10th Marks Type',
    '10th Year of Passout',
    '10th Gap Justification',
    
    // Education - 12th
    '12th Qualification Type',
    '12th University/College',
    '12th Specialization',
    '12th Percentage/CGPA',
    '12th Marks Type',
    '12th Year of Passout',
    '12th Gap Justification',
    
    // Education - B.Tech
    'B.Tech University/College',
    'B.Tech Institute',
    'B.Tech Specialization',
    'B.Tech Percentage/CGPA',
    'B.Tech Marks Type',
    'B.Tech Year of Passout',
    
    // Education - M.Tech
    'M.Tech University/College',
    'M.Tech Specialization',
    'M.Tech Percentage/CGPA',
    'M.Tech Marks Type',
    'M.Tech Year of Passout',
    
    // Education - PhD
    'PhD Done',
    'PhD Year of Passout',
    
    // Education - Highest Degree (Legacy)
    'Highest Degree Type',
    'Graduation University/College',
    'Graduation Institute (B.Tech)',
    'Graduation Specialization',
    'Graduation Percentage/CGPA',
    'Graduation Marks Type',
    'Graduation Year of Passout',
    'Graduation Gap Justification',
    
    // Gap in Education
    'Has Gap in Education',
    'Gap in Education Justification',
    'Has Gap After Highest Education',
    'Gap After Highest Education Justification',
    
    // Diploma
    'Diploma Done',
    'Diploma Stream/Specialization',
    'Diploma Semester',
    
    // Course & Enrollment
    'Course Category',
    'Course Name',
    'Course Code',
    'Domain',
    'Batch Name',
    'Batch Number',
    'Batch Code',
    'Batch Start Date',
    'Course Completion Date',
    'Batch Status',
    'Student Status',
    'Remarks',
    'Mode of Course',
    'Location of Center',
    'Attendance Percentage',
    'Placement Status',
    'In Placement Group',
    
    // Insurance
    'Insurance Taken',
    'Insurance Deactivated',
    
    // Certificate & Testimony
    'Certificate Issued',
    'Video Testimony Received',
    'Text Testimony Received',
    
    // Interview Readiness
    'All Modules Complete',
    'Project Submitted',
    'Readiness (Student View)',
    'Readiness (Trainer View)',
    'Resume Ready and Submitted',
    'Resume Submission Deadline',
    'Resume Reviewed',
    'Resume Changes Needed',
    'Mock Interview Done',
    'Number of Mock Interviews',
    'Communication Skills Rating (1-5)',
    'Attitude Rating (1-5)',
    'Presentation Rating (1-5)',
    'Technical Readiness Rating (1-5)',
    'Areas to Improve',
    'Feedback/Score',
    'Interview Ready',
    
    // Mock Interviews
    'Mock Interview 1 Date',
    'Mock Interview 1 Interviewer Name',
    'Mock Interview 1 Category',
    'Mock Interview 1 Module Name',
    'Mock Interview 1 Feedback',
    'Mock Interview 2 Date',
    'Mock Interview 2 Interviewer Name',
    'Mock Interview 2 Category',
    'Mock Interview 2 Module Name',
    'Mock Interview 2 Feedback',
    
    // Training Concerns
    'Fee Submitted',
    'Fee Payment Issue',
    'Fee Payment Deadline',
    'Login Issues (Course Related)',
    'Login Issue Course Name',
    'Tool Related Issues',
    'Delivery Issues',
    'Module Repeat Needed',
    'Module Repeat Module Name',
    'Placement Related Issues',
    'Preparation Related Issues',
    'General Feedback',
    
    // Counselling Details
    'Counselling Session 1 Date',
    'Counselling Session 1 Counselor Name',
    'Counselling Session 1 Remarks',
    'Counselling Session 2 Date',
    'Counselling Session 2 Counselor Name',
    'Counselling Session 2 Remarks',
    'Counselling Session 3 Date',
    'Counselling Session 3 Counselor Name',
    'Counselling Session 3 Remarks',
    'Counselling Session 4 Date',
    'Counselling Session 4 Counselor Name',
    'Counselling Session 4 Remarks',
    'Follow-Up Needed',
    
    // Placement Opportunities
    'Number of Opportunities',
    'Overall Feedback/Remarks',
    'Opportunity 1 Company Name',
    'Opportunity 1 Date',
    'Opportunity 1 Interview Date',
    'Opportunity 1 Written Test',
    'Opportunity 1 Number of Rounds',
    'Opportunity 1 Rejected at Level',
    'Opportunity 1 Final Status',
    'Opportunity 1 Interview Feedback',
    'Opportunity 2 Company Name',
    'Opportunity 2 Date',
    'Opportunity 2 Interview Date',
    'Opportunity 2 Written Test',
    'Opportunity 2 Number of Rounds',
    'Opportunity 2 Rejected at Level',
    'Opportunity 2 Final Status',
    'Opportunity 2 Interview Feedback',
    
    // Placement Done
    'Placement Company',
    'Placement Position',
    'Placement Package',
    'Placement Location',
    'Placement Interview Date',
    'Placement Date',
    'Placement Result',
    'Placement Offer Details',
    'Placement Joining Date',
    'Should Move to Alumni',
    'Placement Group Removal Reason',
    
    // Additional Information
    'Profile Preference',
    'Motivation',
    'Bio',
    'Notes',
    'Resume URL',
    'Heard From',
    'Created At',
    'Updated At',
    'Is Active'
  ];

  // Convert students to CSV rows
  const rows = students.map(student => {
    const latestEnrollment = student.enrollments && student.enrollments.length > 0 
      ? student.enrollments[0] 
      : null;
    
    const placement = latestEnrollment?.placement || null;
    const batch = latestEnrollment?.batch || null;
    const course = batch?.course || null;
    const category = course?.category || null;
    const domain = student.domain || null;
    
    // Get academic qualifications
    const qualifications = student.academicQualifications || [];
    const tenthQual = getAcademicQualification(qualifications, '10th');
    const twelfthQual = getAcademicQualification(qualifications, '12th');
    const btechQual = getAcademicQualification(qualifications, 'B.Tech');
    const mtechQual = getAcademicQualification(qualifications, 'M.Tech');
    const phdQual = getAcademicQualification(qualifications, 'PhD');
    const highestQual = qualifications.find(q => q.isHighestDegree) || 
                       qualifications.find(q => ['B.Tech', 'B.E', 'B.Sc', 'M.Tech', 'M.E', 'M.Sc'].includes(q.qualificationType)) ||
                       qualifications[qualifications.length - 1] || null;
    
    return [
      escapeCSV(student.id),
      escapeCSV(student.fullName),
      escapeCSV(student.firstName || ''),
      escapeCSV(student.middleName || ''),
      escapeCSV(student.lastName || ''),
      escapeCSV(student.emailAddress),
      escapeCSV(student.phoneNumber),
      escapeCSV(student.phoneNumber), // WhatsApp same as phone
      escapeCSV(student.linkedIn),
      formatDate(student.dob),
      escapeCSV(student.gender),
      escapeCSV(student.employeeId),
      escapeCSV(student.experienceYears),
      escapeCSV(student.availability),
      
      // Address
      escapeCSV(student.currentAddress),
      escapeCSV(student.currentLocation),
      escapeCSV(student.permanentAddress),
      escapeCSV(student.joiningKitAddress),
      escapeCSV(student.joiningKitReceived),
      escapeCSV(student.pincode),
      escapeCSV(student.country?.name || ''),
      escapeCSV(student.state?.name || ''),
      escapeCSV(student.city?.name || ''),
      
      // Guardian/Parent
      escapeCSV(student.guardianName),
      escapeCSV(student.guardianContact),
      escapeCSV(student.guardianEmail),
      escapeCSV(student.parentsName),
      escapeCSV(student.parentsEmail),
      escapeCSV(student.parentsPhone),
      
      // 10th
      escapeCSV(tenthQual?.qualificationType || ''),
      escapeCSV(tenthQual?.universityCollegeName || ''),
      escapeCSV(tenthQual?.specializationBranch || ''),
      escapeCSV(tenthQual?.percentageCgpa || ''),
      escapeCSV(tenthQual?.marksType || ''),
      escapeCSV(tenthQual?.yearOfPassout || ''),
      escapeCSV(tenthQual?.gapJustification || ''),
      
      // 12th
      escapeCSV(twelfthQual?.qualificationType || ''),
      escapeCSV(twelfthQual?.universityCollegeName || ''),
      escapeCSV(twelfthQual?.specializationBranch || ''),
      escapeCSV(twelfthQual?.percentageCgpa || ''),
      escapeCSV(twelfthQual?.marksType || ''),
      escapeCSV(twelfthQual?.yearOfPassout || ''),
      escapeCSV(twelfthQual?.gapJustification || ''),
      
      // B.Tech
      escapeCSV(btechQual?.universityCollegeName || ''),
      escapeCSV(btechQual?.universityCollegeName || ''), // B.Tech Institute
      escapeCSV(btechQual?.specializationBranch || ''),
      escapeCSV(btechQual?.percentageCgpa || ''),
      escapeCSV(btechQual?.marksType || ''),
      escapeCSV(btechQual?.yearOfPassout || ''),
      
      // M.Tech
      escapeCSV(mtechQual?.universityCollegeName || ''),
      escapeCSV(mtechQual?.specializationBranch || ''),
      escapeCSV(mtechQual?.percentageCgpa || ''),
      escapeCSV(mtechQual?.marksType || ''),
      escapeCSV(mtechQual?.yearOfPassout || ''),
      
      // PhD
      escapeCSV(phdQual ? 'Yes' : 'No'),
      escapeCSV(phdQual?.yearOfPassout || ''),
      
      // Highest Degree (Legacy)
      escapeCSV(highestQual?.qualificationType || ''),
      escapeCSV(highestQual?.universityCollegeName || ''),
      escapeCSV(highestQual?.btechInstitute || ''), // If stored separately
      escapeCSV(highestQual?.specializationBranch || ''),
      escapeCSV(highestQual?.percentageCgpa || ''),
      escapeCSV(highestQual?.marksType || ''),
      escapeCSV(highestQual?.yearOfPassout || ''),
      escapeCSV(highestQual?.gapJustification || ''),
      
      // Gap in Education
      escapeCSV(highestQual?.hasGapInEducation ? 'Yes' : 'No'),
      escapeCSV(highestQual?.gapInEducationJustification || ''),
      escapeCSV(highestQual?.hasGapAfterHighestEducation ? 'Yes' : 'No'),
      escapeCSV(highestQual?.gapAfterHighestEducationJustification || ''),
      
      // Diploma
      escapeCSV(student.diplomaDone ? 'Yes' : 'No'),
      escapeCSV(student.stream || ''), // If stored on student
      escapeCSV(student.semester || ''), // If stored on student
      
      // Course & Enrollment
      escapeCSV(category?.categoryName || ''),
      escapeCSV(course?.courseName || ''),
      escapeCSV(course?.courseCode || ''),
      escapeCSV(domain?.domainName || ''),
      escapeCSV(batch?.batchName || ''),
      escapeCSV(batch?.batchNumber || ''),
      escapeCSV(batch?.batchCode || ''),
      formatDate(batch?.startDate),
      formatDate(latestEnrollment?.courseCompletionDate),
      escapeCSV(latestEnrollment?.batchStatus || ''),
      escapeCSV(latestEnrollment?.batchStatus || ''), // Student Status (same as batchStatus)
      escapeCSV(latestEnrollment?.remarks || ''),
      escapeCSV(batch?.mode || ''),
      escapeCSV(batch?.location || ''),
      escapeCSV(latestEnrollment?.attendancePercentage || ''),
      // Map placement status old values to new
      escapeCSV(
        latestEnrollment?.placementStatus === 'Done' ? 'Placed' :
        latestEnrollment?.placementStatus === 'InProcess' ? 'In progress' :
        latestEnrollment?.placementStatus === 'Not Started' ? 'Not interested' :
        latestEnrollment?.placementStatus || ''
      ),
      escapeCSV(latestEnrollment?.inPlacementGroup ? 'Yes' : 'No'),
      
      // Insurance
      escapeCSV(latestEnrollment?.insuranceTaken ? 'Yes' : 'No'),
      escapeCSV(latestEnrollment?.insuranceDeactivated ? 'Yes' : 'No'),
      
      // Certificate & Testimony
      escapeCSV(latestEnrollment?.certificationIssued ? 'Yes' : 'No'),
      escapeCSV(latestEnrollment?.videoTestimonyReceived ? 'Yes' : 'No'),
      escapeCSV(latestEnrollment?.testimonyReceived ? 'Yes' : 'No'),
      
      // Interview Readiness
      escapeCSV(latestEnrollment?.interviewReadiness?.allModulesComplete ? 'Yes' : 'No'),
      escapeCSV(latestEnrollment?.interviewReadiness?.projectSubmitted ? 'Yes' : 'No'),
      escapeCSV(latestEnrollment?.interviewReadiness?.studentReadiness ? 'Yes' : 'No'),
      escapeCSV(latestEnrollment?.interviewReadiness?.trainerReadiness ? 'Yes' : 'No'),
      escapeCSV(latestEnrollment?.interviewReadiness?.resumeReadySubmitted ? 'Yes' : 'No'),
      formatDate(latestEnrollment?.interviewReadiness?.resumeSubmissionDeadline),
      escapeCSV(latestEnrollment?.interviewReadiness?.resumeReviewed ? 'Yes' : 'No'),
      escapeCSV(latestEnrollment?.interviewReadiness?.resumeChangesNeeded ? 'Yes' : 'No'),
      escapeCSV(latestEnrollment?.interviewReadiness?.mockInterviewDone ? 'Yes' : 'No'),
      escapeCSV(latestEnrollment?.interviewReadiness?.numberOfMockInterviews || 0),
      escapeCSV(latestEnrollment?.interviewReadiness?.communicationSkillsRating || ''),
      escapeCSV(latestEnrollment?.interviewReadiness?.attitudeRating || ''),
      escapeCSV(latestEnrollment?.interviewReadiness?.presentationRating || ''),
      escapeCSV(latestEnrollment?.interviewReadiness?.technicalReadinessRating || ''),
      escapeCSV(latestEnrollment?.interviewReadiness?.areasToImprove || ''),
      escapeCSV(latestEnrollment?.interviewReadiness?.feedbackScore || ''),
      escapeCSV(latestEnrollment?.interviewReadiness?.interviewReady ? 'Yes' : 'No'),
      
      // Mock Interviews
      ...(() => {
        const mock1 = latestEnrollment?.mockInterviews?.find(m => m.interviewNumber === 1);
        return [
          escapeCSV(mock1 ? formatDate(mock1.interviewDate) : ''),
          escapeCSV(mock1?.interviewerName || ''),
          escapeCSV(mock1?.interviewCategory || ''),
          escapeCSV(mock1?.moduleName || ''),
          escapeCSV(mock1?.feedback || '')
        ];
      })(),
      ...(() => {
        const mock2 = latestEnrollment?.mockInterviews?.find(m => m.interviewNumber === 2);
        return [
          escapeCSV(mock2 ? formatDate(mock2.interviewDate) : ''),
          escapeCSV(mock2?.interviewerName || ''),
          escapeCSV(mock2?.interviewCategory || ''),
          escapeCSV(mock2?.moduleName || ''),
          escapeCSV(mock2?.feedback || '')
        ];
      })(),
      
      // Training Concerns
      escapeCSV(latestEnrollment?.feeDetails?.feeSubmitted ? 'Yes' : 'No'),
      escapeCSV(latestEnrollment?.feeDetails?.paymentIssue || ''),
      formatDate(latestEnrollment?.feeDetails?.paymentDeadline),
      (() => {
        const loginConcern = latestEnrollment?.trainingConcerns?.find(tc => tc.concernType === 'Login');
        return escapeCSV(loginConcern ? 'Yes' : 'No');
      })(),
      (() => {
        const loginConcern = latestEnrollment?.trainingConcerns?.find(tc => tc.concernType === 'Login');
        return escapeCSV(loginConcern?.issueDetails || '');
      })(),
      (() => {
        const toolConcern = latestEnrollment?.trainingConcerns?.find(tc => tc.concernType === 'Tool');
        return escapeCSV(toolConcern ? 'Yes' : 'No');
      })(),
      (() => {
        const deliveryConcern = latestEnrollment?.trainingConcerns?.find(tc => tc.concernType === 'Delivery');
        return escapeCSV(deliveryConcern ? 'Yes' : 'No');
      })(),
      (() => {
        const moduleConcern = latestEnrollment?.trainingConcerns?.find(tc => tc.concernType === 'Module Repeat');
        return escapeCSV(moduleConcern ? 'Yes' : 'No');
      })(),
      (() => {
        const moduleConcern = latestEnrollment?.trainingConcerns?.find(tc => tc.concernType === 'Module Repeat');
        return escapeCSV(moduleConcern?.issueDetails || '');
      })(),
      (() => {
        const placementConcern = latestEnrollment?.trainingConcerns?.find(tc => tc.concernType === 'Placement');
        return escapeCSV(placementConcern ? 'Yes' : 'No');
      })(),
      (() => {
        const prepConcern = latestEnrollment?.trainingConcerns?.find(tc => tc.concernType === 'Preparation');
        return escapeCSV(prepConcern ? 'Yes' : 'No');
      })(),
      (() => {
        const generalConcern = latestEnrollment?.trainingConcerns?.find(tc => tc.concernType === 'General');
        return escapeCSV(generalConcern?.description || '');
      })(),
      
      // Counselling Details
      ...(() => {
        const session1 = latestEnrollment?.counsellingSessions?.[0];
        return [
          escapeCSV(session1 ? formatDate(session1.sessionDate) : ''),
          escapeCSV(session1?.counselor?.counselorName || ''),
          escapeCSV(session1?.remarks || '')
        ];
      })(),
      ...(() => {
        const session2 = latestEnrollment?.counsellingSessions?.[1];
        return [
          escapeCSV(session2 ? formatDate(session2.sessionDate) : ''),
          escapeCSV(session2?.counselor?.counselorName || ''),
          escapeCSV(session2?.remarks || '')
        ];
      })(),
      ...(() => {
        const session3 = latestEnrollment?.counsellingSessions?.[2];
        return [
          escapeCSV(session3 ? formatDate(session3.sessionDate) : ''),
          escapeCSV(session3?.counselor?.counselorName || ''),
          escapeCSV(session3?.remarks || '')
        ];
      })(),
      ...(() => {
        const session4 = latestEnrollment?.counsellingSessions?.[3];
        return [
          escapeCSV(session4 ? formatDate(session4.sessionDate) : ''),
          escapeCSV(session4?.counselor?.counselorName || ''),
          escapeCSV(session4?.remarks || '')
        ];
      })(),
      (() => {
        const lastSession = latestEnrollment?.counsellingSessions?.[latestEnrollment?.counsellingSessions?.length - 1];
        return escapeCSV(lastSession?.followUpNeeded ? 'Yes' : 'No');
      })(),
      
      // Placement Opportunities
      escapeCSV(latestEnrollment?.placementOpportunities?.length || 0),
      escapeCSV(''), // Overall Feedback/Remarks - would need to be stored separately
      ...(() => {
        const opp1 = latestEnrollment?.placementOpportunities?.find(o => o.opportunityNumber === 1);
        return [
          escapeCSV(opp1?.company?.companyName || ''),
          escapeCSV(opp1 ? formatDate(opp1.date) : ''),
          escapeCSV(opp1 ? formatDate(opp1.interviewDate) : ''),
          escapeCSV(opp1?.writtenTest ? 'Yes' : 'No'),
          escapeCSV(opp1?.numberOfRounds || ''),
          escapeCSV(opp1?.rejectedAtLevel || ''),
          escapeCSV(opp1?.finalStatus || ''),
          escapeCSV(opp1?.interviewFeedback || '')
        ];
      })(),
      ...(() => {
        const opp2 = latestEnrollment?.placementOpportunities?.find(o => o.opportunityNumber === 2);
        return [
          escapeCSV(opp2?.company?.companyName || ''),
          escapeCSV(opp2 ? formatDate(opp2.date) : ''),
          escapeCSV(opp2 ? formatDate(opp2.interviewDate) : ''),
          escapeCSV(opp2?.writtenTest ? 'Yes' : 'No'),
          escapeCSV(opp2?.numberOfRounds || ''),
          escapeCSV(opp2?.rejectedAtLevel || ''),
          escapeCSV(opp2?.finalStatus || ''),
          escapeCSV(opp2?.interviewFeedback || '')
        ];
      })(),
      
      // Placement Done
      
      // Placement
      escapeCSV(placement?.company?.companyName || ''),
      escapeCSV(placement?.jobRole || ''),
      escapeCSV(placement?.startingPackage || placement?.currentPackage || ''),
      escapeCSV(placement?.feedbackRemarks?.match(/Location:\s*([^|]+)/i)?.[1]?.trim() || ''),
      formatDate(placement?.interviewDate),
      formatDate(placement?.placementDate),
      escapeCSV(placement?.feedbackRemarks?.match(/Interview Result:\s*([^|]+)/i)?.[1]?.trim() || ''),
      escapeCSV(placement?.feedbackRemarks?.match(/Offer Details:\s*(.+)/i)?.[1]?.trim() || ''),
      formatDate(placement?.joiningDate),
      escapeCSV(placement?.shouldMoveToAlumni ? 'Yes' : 'No'),
      escapeCSV(placement?.placementGroupRemovalReason || ''),
      
      // Additional
      escapeCSV(student.profilePreference || ''),
      escapeCSV(student.motivation || ''),
      escapeCSV(student.bio || ''),
      escapeCSV(student.notes || ''),
      escapeCSV(student.resumeUrl || ''),
      escapeCSV(student.heardFrom?.value || ''),
      formatDate(student.createdAt),
      formatDate(student.updatedAt),
      escapeCSV(student.isActive ? 'Yes' : 'No')
    ];
  });

  // Combine headers and rows
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');

  return csvContent;
};

/**
 * Download CSV file
 */
export const downloadCSV = (csvContent, filename = 'students_export.csv') => {
  // Add BOM for Excel compatibility
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Clean up
  URL.revokeObjectURL(url);
};


